import { Chess } from 'chess.js';
import type { AnalysisResult, EngineLine, EngineStatus } from '../../types';
import { normalizeScoreToWhitePov, parseBestMove, parseInfoLine } from './uci';

export interface AnalyzeOptions {
  depth?: number; // profondeur cible (par défaut ANALYSIS_DEPTH)
  multiPv?: number; // nombre de lignes candidates (par défaut 1)
  timeoutMs?: number; // garde-fou : on retourne le meilleur résultat obtenu même hors profondeur
  signal?: AbortSignal; // permet d'annuler une analyse en cours
}

const ENGINE_URL = '/engine/stockfish-18-lite-single.js';
export const DEFAULT_DEPTH = 16;
export const DEFAULT_TIMEOUT_MS = 15000;

type StatusListener = (status: EngineStatus) => void;

/**
 * Client Stockfish exécuté dans un Web Worker dédié. Ne bloque jamais le
 * thread principal (l'UI reste réactive pendant l'analyse). Une seule
 * analyse à la fois : une nouvelle requête annule automatiquement la
 * précédente pour qu'une ancienne analyse ne puisse jamais écraser le
 * résultat d'une nouvelle partie/position.
 */
export class StockfishEngine {
  private worker: Worker | null = null;
  private status: EngineStatus = 'idle';
  private listeners = new Set<StatusListener>();
  private readyPromise: Promise<void> | null = null;
  private currentRequestId = 0;
  private pendingCancel: (() => void) | null = null;

  getStatus(): EngineStatus {
    return this.status;
  }

  onStatusChange(listener: StatusListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setStatus(status: EngineStatus) {
    this.status = status;
    for (const l of this.listeners) l(status);
  }

  /** Initialise le worker et effectue le handshake UCI. Idempotent. */
  async init(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;

    this.readyPromise = new Promise<void>((resolve, reject) => {
      this.setStatus('loading');
      let worker: Worker;
      try {
        worker = new Worker(ENGINE_URL);
      } catch (err) {
        this.setStatus('error');
        reject(new Error(`Impossible de charger le moteur Stockfish (${describeError(err)}).`));
        return;
      }
      this.worker = worker;

      const onError = (ev: ErrorEvent | Event) => {
        this.setStatus('error');
        reject(new Error(`Le moteur Stockfish a échoué à démarrer (${describeError(ev)}).`));
      };
      worker.addEventListener('error', onError, { once: true });

      const onUciOk = (ev: MessageEvent<string>) => {
        if (typeof ev.data === 'string' && ev.data.startsWith('uciok')) {
          worker.removeEventListener('message', onUciOk);
          worker.postMessage('setoption name Threads value 1');
          worker.postMessage('isready');
          const onReady = (ev2: MessageEvent<string>) => {
            if (typeof ev2.data === 'string' && ev2.data.startsWith('readyok')) {
              worker.removeEventListener('message', onReady);
              worker.removeEventListener('error', onError);
              this.setStatus('ready');
              resolve();
            }
          };
          worker.addEventListener('message', onReady);
        }
      };
      worker.addEventListener('message', onUciOk);
      worker.postMessage('uci');
    });

    return this.readyPromise;
  }

  /**
   * Analyse une position FEN. Détecte d'abord les positions terminales
   * (mat/pat) sans solliciter le moteur. Sinon, lance une recherche bornée
   * en profondeur avec un timeout : au-delà, on retourne le meilleur
   * résultat obtenu jusque-là en le marquant `timedOut: true`, plutôt que
   * d'échouer silencieusement.
   */
  async analyze(fen: string, options: AnalyzeOptions = {}): Promise<AnalysisResult> {
    const terminal = detectTerminal(fen);
    if (terminal) {
      return {
        fen,
        bestLines: [],
        depthReached: 0,
        timedOut: false,
        isTerminal: true,
        terminalReason: terminal,
      };
    }

    await this.init();
    if (!this.worker) throw new Error('Moteur non initialisé.');

    // Une seule analyse à la fois : si une requête précédente est encore en
    // vol (ce qui ne devrait pas arriver avec un usage séquentiel normal),
    // on la fait échouer explicitement plutôt que de la laisser bloquée
    // indéfiniment (une ancienne analyse ne doit jamais écraser la nouvelle,
    // mais elle ne doit pas non plus rester en attente pour toujours).
    this.pendingCancel?.();

    const requestId = ++this.currentRequestId;
    const depth = options.depth ?? DEFAULT_DEPTH;
    const multiPv = options.multiPv ?? 1;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const worker = this.worker;
    const sideToMove = fen.split(' ')[1] === 'b' ? 'b' : 'w';

    this.setStatus('analyzing');

    return new Promise<AnalysisResult>((resolve, reject) => {
      const linesByMultiPv = new Map<number, EngineLine>();
      let depthReached = 0;
      let settled = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

      const cleanup = () => {
        worker.removeEventListener('message', onMessage);
        if (timeoutHandle) clearTimeout(timeoutHandle);
        if (options.signal) options.signal.removeEventListener('abort', onAbort);
        if (this.pendingCancel === cancelThisRequest) this.pendingCancel = null;
      };

      const isStale = () => requestId !== this.currentRequestId;

      const finish = (timedOut: boolean) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (isStale()) {
          // Une analyse plus récente a pris le relais : on n'écrase rien,
          // on abandonne simplement silencieusement ce résultat obsolète.
          return;
        }
        this.setStatus('ready');
        const bestLines = [...linesByMultiPv.values()].sort(
          (a, b) => scoreRank(b.score) - scoreRank(a.score),
        );
        resolve({ fen, bestLines, depthReached, timedOut, isTerminal: false });
      };

      const onAbort = () => {
        if (settled) return;
        settled = true;
        cleanup();
        worker.postMessage('stop');
        this.setStatus(isStale() ? this.status : 'cancelled');
        reject(new DOMException('Analyse annulée.', 'AbortError'));
      };

      // Permet à une requête plus récente de faire échouer proprement
      // celle-ci si jamais elle est encore en attente (cf. garde-fou ci-dessus).
      function cancelThisRequest() {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new DOMException('Analyse remplacée par une requête plus récente.', 'AbortError'));
      }
      this.pendingCancel = cancelThisRequest;

      const onMessage = (ev: MessageEvent<string>) => {
        if (isStale()) return;
        const data = ev.data;
        if (typeof data !== 'string') return;

        const info = parseInfoLine(data);
        if (info) {
          const pvIndexMatch = data.match(/\bmultipv (\d+)/);
          const pvIndex = pvIndexMatch ? Number(pvIndexMatch[1]) : 1;
          const normalized: EngineLine = {
            ...info,
            score: normalizeScoreToWhitePov(info.score, sideToMove),
          };
          linesByMultiPv.set(pvIndex, normalized);
          depthReached = Math.max(depthReached, info.depth);
          return;
        }

        const best = parseBestMove(data);
        if (best !== null || data.startsWith('bestmove')) {
          worker.postMessage('stop'); // no-op si déjà arrêté, garde-fou
          finish(false);
        }
      };

      worker.addEventListener('message', onMessage);
      if (options.signal) {
        if (options.signal.aborted) {
          onAbort();
          return;
        }
        options.signal.addEventListener('abort', onAbort, { once: true });
      }

      timeoutHandle = setTimeout(() => {
        if (settled) return;
        worker.postMessage('stop');
        // Stockfish répond à "stop" par un "bestmove" ; on laisse une marge
        // courte pour le recevoir avant de conclure au timeout sec.
        timeoutHandle = setTimeout(() => finish(true), 500);
      }, timeoutMs);

      worker.postMessage(`position fen ${fen}`);
      worker.postMessage(`setoption name MultiPV value ${multiPv}`);
      worker.postMessage(`go depth ${depth}`);
    });
  }

  terminate() {
    this.worker?.postMessage('quit');
    this.worker?.terminate();
    this.worker = null;
    this.readyPromise = null;
    this.setStatus('idle');
  }
}

function scoreRank(score: EngineLine['score']): number {
  if (score.type === 'cp') return score.value;
  const MATE_BASE = 100000;
  return score.value > 0 ? MATE_BASE - score.value : -MATE_BASE - score.value;
}

function detectTerminal(fen: string): 'checkmate' | 'stalemate' | 'draw' | null {
  try {
    const chess = new Chess(fen);
    if (chess.isCheckmate()) return 'checkmate';
    if (chess.isStalemate()) return 'stalemate';
    if (chess.isDraw()) return 'draw';
    return null;
  } catch {
    return null;
  }
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
  return 'erreur inconnue';
}

/** Instance partagée (une seule analyse à la fois, ce qui suffit en V1). */
export const sharedEngine = new StockfishEngine();
